import { useCallback } from "react";
import { confirmDeletePost } from "../../components/confirmations";
import { useQueryClient } from "@tanstack/react-query";
import type { FeedItem, Page, Post, PostReaction } from "@atrium/shared";
import { api, errorMessage } from "../../lib/api";
import { queryClient, removePostEverywhere, updatePostEverywhere, type Infinite } from "../../lib/queryClient";
import { toast } from "../../components/ui/Toast";
import { confirm } from "../../components/ui/Dialog";

/** Snapshot feed caches so a removal can be undone exactly. */
function snapshotFeeds() {
  return queryClient.getQueriesData<Infinite<Page<FeedItem>>>({ queryKey: ["feed"] });
}
function restoreFeeds(snap: ReturnType<typeof snapshotFeeds>) {
  for (const [key, data] of snap) queryClient.setQueryData(key, data);
}
/** Drop every feed item matching `match` (a post, or every post by an author). */
function dropFromFeeds(match: (item: FeedItem) => boolean) {
  queryClient.setQueriesData<Infinite<Page<FeedItem>>>({ queryKey: ["feed"] }, (old) =>
    old ? { ...old, pages: old.pages.map((p) => ({ ...p, items: p.items.filter((i) => !match(i)) })) } : old,
  );
}

/** Optimistic post interactions shared by every surface that shows posts. */
export function usePostActions() {
  const qc = useQueryClient();

  /** React with `kind`, or remove the reaction with null. */
  const react = useCallback(async (post: Post, kind: PostReaction | null) => {
    const value = kind !== null;
    const prev = { liked: post.liked, myReaction: post.myReaction, likeCount: post.likeCount };
    const delta = value === post.liked ? 0 : value ? 1 : -1;
    updatePostEverywhere(post.id, (p) => ({ ...p, liked: value, myReaction: kind, likeCount: Math.max(0, p.likeCount + delta) }));
    try {
      const r = await api.put<{ likeCount: number; myReaction: PostReaction | null }>(`/api/posts/${post.id}/like`, { value, kind: kind ?? "like" });
      updatePostEverywhere(post.id, (p) => ({ ...p, liked: value, myReaction: r.myReaction, likeCount: r.likeCount }));
    } catch (e) {
      updatePostEverywhere(post.id, (p) => ({ ...p, ...prev }));
      toast.error(errorMessage(e, "Couldn't update your reaction"));
    }
  }, []);

  const like = useCallback((post: Post) => react(post, post.liked ? null : "like"), [react]);

  const repost = useCallback(
    async (post: Post) => {
      const value = !post.reposted;
      updatePostEverywhere(post.id, (p) => ({ ...p, reposted: value, repostCount: p.repostCount + (value ? 1 : -1) }));
      try {
        const r = await api.put<{ repostCount: number }>(`/api/posts/${post.id}/repost`, { value });
        updatePostEverywhere(post.id, (p) => ({ ...p, reposted: value, repostCount: r.repostCount }));
        void qc.invalidateQueries({ queryKey: ["profile-posts"] });
        if (value)
          toast.show("Reposted", {
            label: "Undo",
            onClick: () => void repost({ ...post, reposted: true }),
          });
      } catch (e) {
        updatePostEverywhere(post.id, (p) => ({ ...p, reposted: !value, repostCount: p.repostCount + (value ? -1 : 1) }));
        toast.error(errorMessage(e, "Couldn't repost"));
      }
    },
    [qc],
  );

  const remove = useCallback(
    async (post: Post) => {
      const ok = await confirmDeletePost({ hasReplies: post.commentCount > 0, isQuoted: post.repostCount > 0 });
      if (!ok) return false;
      try {
        await api.del(`/api/posts/${post.id}`);
        removePostEverywhere(post.id);
        qc.removeQueries({ queryKey: ["post", post.id] });
        void qc.invalidateQueries({ queryKey: ["profile"] });
        return true;
      } catch (e) {
        toast.error(errorMessage(e, "Couldn't delete post"));
        return false;
      }
    },
    [qc],
  );

  const pin = useCallback(
    async (post: Post) => {
      const value = !post.pinned;
      try {
        await api.put(`/api/posts/${post.id}/pin`, { value });
        updatePostEverywhere(post.id, (p) => ({ ...p, pinned: value }));
        void qc.invalidateQueries({ queryKey: ["profile-posts"] });
        toast.show(value ? "Pinned to your profile" : "Unpinned");
      } catch (e) {
        toast.error(errorMessage(e));
      }
    },
    [qc],
  );

  /** Save to bookmarks (optionally into a collection), or remove. */
  const bookmark = useCallback(
    async (post: Post, opts: { collectionId?: string | null; collectionName?: string; remove?: boolean } = {}) => {
      const value = !opts.remove;
      updatePostEverywhere(post.id, (p) => ({ ...p, bookmarked: value }));
      try {
        if (value) await api.put("/api/bookmarks", { targetType: "post", targetId: post.id, ...(opts.collectionId !== undefined ? { collectionId: opts.collectionId } : {}) });
        else await api.del(`/api/bookmarks/post/${post.id}`);
        void qc.invalidateQueries({ queryKey: ["saved"] });
        void qc.invalidateQueries({ queryKey: ["collections"] });
        if (value && opts.collectionName) toast.show(`Saved to ${opts.collectionName}`);
        else if (!value) toast.show("Removed from saved", { label: "Undo", onClick: () => void bookmark(post, { collectionId: opts.collectionId }) });
      } catch (e) {
        updatePostEverywhere(post.id, (p) => ({ ...p, bookmarked: !value }));
        toast.error(errorMessage(e, "Couldn't update saved posts"));
      }
    },
    [qc],
  );

  /** "Hide" or "Not interested": leaves the feed at once, with undo. */
  const feedback = useCallback(async (post: Post, kind: "hide" | "not_interested") => {
    const snap = snapshotFeeds();
    dropFromFeeds((i) => i.post.id === post.id);
    try {
      await api.put(`/api/posts/${post.id}/feedback`, { kind });
      toast.show(kind === "hide" ? "Post hidden" : "You'll see fewer posts like this", {
        label: "Undo",
        onClick: async () => {
          restoreFeeds(snap);
          await api.put(`/api/posts/${post.id}/feedback`, { kind: null }).catch((e) => toast.error(errorMessage(e)));
        },
      });
    } catch (e) {
      restoreFeeds(snap);
      toast.error(errorMessage(e));
    }
  }, []);

  /** Mute the author: their posts leave the feed. Undoable. */
  const muteAuthor = useCallback(
    async (post: Post) => {
      const who = post.author;
      const snap = snapshotFeeds();
      dropFromFeeds((i) => i.post.author.id === who.id || i.repostedBy?.id === who.id);
      try {
        await api.put(`/api/relations/${who.id}/mute`, { value: true });
        void qc.invalidateQueries({ queryKey: ["relations"] });
        toast.show(`Muted ${who.displayName}`, {
          label: "Undo",
          onClick: async () => {
            restoreFeeds(snap);
            await api.put(`/api/relations/${who.id}/mute`, { value: false }).catch((e) => toast.error(errorMessage(e)));
            void qc.invalidateQueries({ queryKey: ["relations"] });
          },
        });
      } catch (e) {
        restoreFeeds(snap);
        toast.error(errorMessage(e));
      }
    },
    [qc],
  );

  /** Block the author. Severs follows and friendship, so it asks first. */
  const blockAuthor = useCallback(
    async (post: Post) => {
      const who = post.author;
      const ok = await confirm({
        title: `Block ${who.displayName}?`,
        description: "You won't see each other's posts, comments or messages. Follows and friendship between you are removed.",
        confirmLabel: "Block",
        danger: true,
      });
      if (!ok) return;
      dropFromFeeds((i) => i.post.author.id === who.id || i.repostedBy?.id === who.id);
      try {
        await api.put(`/api/relations/${who.id}/block`, { value: true });
        void qc.invalidateQueries({ queryKey: ["relations"] });
        void qc.invalidateQueries({ queryKey: ["profile"] });
        toast.show(`Blocked ${who.displayName}`, {
          label: "Undo",
          onClick: async () => {
            await api.put(`/api/relations/${who.id}/block`, { value: false }).catch((e) => toast.error(errorMessage(e)));
            void qc.invalidateQueries({ queryKey: ["feed"] });
            void qc.invalidateQueries({ queryKey: ["relations"] });
          },
        });
      } catch (e) {
        void qc.invalidateQueries({ queryKey: ["feed"] });
        toast.error(errorMessage(e));
      }
    },
    [qc],
  );

  const share = useCallback(async (post: Post) => {
    const url = `${window.location.origin}/p/${post.id}`;
    if (typeof navigator.share === "function") {
      try {
        await navigator.share({ url, title: `Post by ${post.author.displayName}` });
        return;
      } catch (e) {
        if ((e as Error).name === "AbortError") return;
        /* not allowed here: fall back to copying */
      }
    }
    await copyText(url);
    toast.show("Link copied");
  }, []);

  return { like, react, repost, remove, pin, bookmark, feedback, muteAuthor, blockAuthor, share };
}

export async function copyText(text: string) {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const ta = document.createElement("textarea");
    ta.value = text;
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    ta.remove();
  }
}
